import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  Ref,
  useEffect,
} from "react";

import { Box } from "@mui/material";
import ReactPlayer from "react-player";

import { VideoPlayerState } from "../types/VideoPlayerState";

interface VideoPlayerProps {
  src: string;
  videoPlayerState: VideoPlayerState;
  onUpdatePlayerState: (state: VideoPlayerState) => void;
}

export interface VideoPreviewPlayerHandle {
  seekTo: (time: number) => void;
  play: () => void;
  pause: () => void;
  isPlaying: () => boolean;
}

const VideoPreviewPlayer = forwardRef<
  VideoPreviewPlayerHandle,
  VideoPlayerProps
>(
  (
    { src, videoPlayerState, onUpdatePlayerState }: VideoPlayerProps,
    ref: Ref<VideoPreviewPlayerHandle>,
  ) => {
    const playerRef = useRef<ReactPlayer>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { isPlaying } = videoPlayerState;

    // Monitor state changes
    useEffect(() => {}, [isPlaying]);

    const togglePlay = (): void => {
      onUpdatePlayerState({
        ...videoPlayerState,
        isPlaying: !isPlaying,
      });
    };

    const handleProgress = ({
      playedSeconds,
    }: {
      playedSeconds: number;
    }): void => {
      onUpdatePlayerState({
        ...videoPlayerState,
        currentTime: playedSeconds,
      });
    };

    const handlePlay = (): void => {
      onUpdatePlayerState({
        ...videoPlayerState,
        isPlaying: true,
      });
    };

    const handlePause = (): void => {
      onUpdatePlayerState({
        ...videoPlayerState,
        isPlaying: false,
      });
    };

    const handleEnded = (): void => {
      onUpdatePlayerState({
        ...videoPlayerState,
        isPlaying: false, // Ensure isPlaying is set to false when video ends
        // Don't reset currentTime to 0 - keep it at the end
      });
    };

    // Expose the seekTo method to the parent component
    useImperativeHandle(ref, () => ({
      seekTo: (time: number): void => {
        if (playerRef.current) {
          playerRef.current.seekTo(time);
        }
      },
      play: (): void => {
        if (playerRef.current) {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer && internalPlayer.paused) {
            internalPlayer.play();
            onUpdatePlayerState({
              ...videoPlayerState,
              isPlaying: true,
            });
          }
        }
      },
      pause: (): void => {
        if (playerRef.current) {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer && !internalPlayer.paused) {
            internalPlayer.pause();
            onUpdatePlayerState({
              ...videoPlayerState,
              isPlaying: false,
            });
          }
        }
      },
      isPlaying: (): boolean => {
        if (playerRef.current) {
          return !playerRef.current.getInternalPlayer()?.paused || false;
        }
        return false;
      },
    }));

    return (
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          bgcolor: "common.black",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            aspectRatio: "16/9",
            position: "relative",
          }}
        >
          <ReactPlayer
            ref={playerRef}
            url={src}
            playing={isPlaying}
            onProgress={handleProgress}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            width="100%"
            height="100%"
            onClick={togglePlay}
            controls
            config={{
              file: {
                attributes: {
                  controlsList: "nodownload",
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  },
);

export default VideoPreviewPlayer;
